import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { getValue } from '@glimmer/tracking/primitives/cache';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { invokeHelper } from '@ember/helper';
import { on } from '@ember/modifier';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { task } from 'ember-concurrency';
import { or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsTextDisplay,
  HdsTextBody,
  HdsLinkInline,
  HdsButton,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  FlexGrid,
  WithErrors,
} from 'core/components';
import { rbacRoles } from 'core/helpers';
import IamPolicy from 'core/utils/iam-policy';
import { TYPE_ORGANIZATION } from 'common/utils/cloud-resource-types';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';
import TerraformPermissions from 'hcp/components/hcp/alert/terraform-permissions';
import BasicRoleSelect from 'hcp/components/iam/invitations/form/basic-role-select';
import NoRoleWithProjects from 'hcp/components/iam/invitations/alert/no-role-with-projects';

/**
 * An object representing the current location of roles, with link and roles.
 * Required for the RoleDefinitionsFlyout.
 * @argument currentRolesLocation
 * @type {Object}
 */
/**
 * An object representing the current scope, with properties type, name, and id.
 * Required for the RoleDefinitionsFlyout.
 * @argument currentScopeLink
 * @type {Object}
 */
/**
 * The organization.
 * @argument organization
 * @type {Object}
 */
/**
 * The organization policy.
 * @argument organizationPolicy
 * @type {Object}
 */
/**
 * The user principal.
 * @argument userPrincipal
 * @type {Object}
 */
/**
 * A flag for if scim is enabled.
 * @argument scimEnabled
 * @type {Object}
 */
/**
 *
 * `PageUsersEditAssignment` allows the editing of a user principal's basic role.
 *
 * ```
 * <Page::Users::EditAssignment
 *   @currentRolesLocation={{@currentRolesLocation}}
 *   @currentScopeLink={{@currentScopeLink}}
 *   @organization={{this.model.organization}}
 *   @policy={{this.model.policy}}
 *   @redirectRoute={{this.model.redirectRoute}}
 *   @userPrincipal={{this.model.userPrincipal}}
 *   @scimEnabled={{this.model.scimEnabled}}
 * />
 * ```
 *
 * @class PageUsersEditAssignment
 *
 */
export default class PageUsersEditAssignment extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service router;
  @service userContext;

  @tracked updatedRoleId = this.organizationRoleId;
  @tracked policy = new IamPolicy(this.args.organizationPolicy);
  @tracked showRoleDefinitionsFlyout = false;

  get OrganizationPolicy() {
    return this.policy;
  }

  get organizationRoleId() {
    if (!this.OrganizationPolicy) {
      return undefined;
    }

    const { userPrincipal } = this.args;
    const { roleId } = this.OrganizationPolicy.getMemberById(userPrincipal.id);
    return roleId;
  }

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();

    const {
      organization,
      redirectRoute = 'cloud.access-control.users.list',
      userPrincipal,
    } = this.args;

    if (this.organizationRoleId == this.updatedRoleId) {
      // Do nothing if the role has not changed.
      return;
    }

    // Get the current organization policy.
    const UpdatedOrganizationPolicy = this.OrganizationPolicy;

    if (!this.updatedRoleId) {
      // Remove the user from the policy if we're selecting no role.
      UpdatedOrganizationPolicy.removeMemberById(userPrincipal.id);
    } else {
      const { memberId } = UpdatedOrganizationPolicy.getMemberById(
        userPrincipal.id
      );
      if (!memberId) {
        // Add this member to the policy with their new role.
        UpdatedOrganizationPolicy.addMember(
          userPrincipal.id,
          HashicorpCloudResourcemanagerPolicyBindingMemberType.USER,
          this.updatedRoleId
        );
      } else {
        // Update the policy with the new role for this USER.
        UpdatedOrganizationPolicy.changeMemberRole(
          userPrincipal.id,
          this.updatedRoleId
        );
      }
    }

    const payload = {
      policy: UpdatedOrganizationPolicy.get(),
    };

    try {
      const { policy } =
        await this.api.resourceManager.org.organizationServiceSetIamPolicy(
          organization.id,
          payload
        );

      // Update the local policy to get the new etag. Without this, the component
      // state will become out-of-date and will return conflicts when trying
      // to update.
      this.policy = new IamPolicy(policy);

      if (!this.updatedRoleId) {
        this.flashMessages.success(
          this.intl.t(
            'components.page.access-control.users.edit-assignment.org-role-updated-successfully'
          ),
          {
            actionText: this.intl.t(
              'components.page.access-control.users.edit-assignment.go-to-projects'
            ),
            content: this.intl.t(
              'components.page.access-control.users.edit-assignment.user-no-longer-has-role',
              {
                userName: userPrincipal.fullName || userPrincipal.email,
                htmlSafe: true,
              }
            ),
            onAction: () => {
              return this.router.transitionTo(
                'cloud.orgs.detail.projects',
                organization.id
              );
            },
            linkIcon: 'arrow-right',
            linkIconPosition: 'trailing',
            linkText: this.intl.t(
              'components.page.access-control.users.edit-assignment.learn-more'
            ),
            linkUrl:
              'https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/users#project-role',
          }
        );
      } else {
        const role = getValue(
          invokeHelper(this, rbacRoles, () => {
            return {
              positional: [this.updatedRoleId],
              named: {
                active: true,
                type: TYPE_ORGANIZATION,
              },
            };
          })
        );

        const userName = userPrincipal.fullName || userPrincipal.email;
        this.flashMessages.success(
          this.intl.t(
            'components.page.access-control.users.edit-assignment.org-role-updated-successfully'
          ),
          {
            content: this.intl.t(
              'components.page.access-control.users.edit-assignment.the-role-for-user-is-now',
              {
                htmlSafe: true,
                roleLabel: role?.label || this.updatedRoleId,
                userName,
              }
            ),
          }
        );
      }

      const container = getOwner(this);
      const route = container.lookup(`route:${redirectRoute}`);

      if (route) {
        // We need to refresh this before transitioning because if this is a
        // child route, the parent route will not call the model hook again when
        // transitioning upward even if data was changed in the child.
        route.refresh();
        this.router.transitionTo(`${redirectRoute}`);
      }
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      this.flashMessages.error(
        this.intl.t(
          'components.page.access-control.users.edit-assignment.org-role-update-failed'
        ),
        {
          content: this.intl.t(
            'components.page.access-control.users.edit-assignment.something-went-wrong'
          ),
          linkIcon: 'help',
          linkIconPosition: 'leading',
          linkText: this.intl.t(
            'components.page.access-control.users.edit-assignment.contact-support'
          ),
          linkUrl: this.supportTicketHref,
        }
      );
    }
  });

  <template>
    <WithErrors @error={{this.save.last.error}} as |FormError|>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.IconTile @icon="user" />
            <PH.Title>
              {{or @userPrincipal.fullName @userPrincipal.email}}
            </PH.Title>
            <PH.Description>
              {{t
                "components.page.access-control.users.edit-assignment.edit-role-assignment"
              }}
            </PH.Description>
          </HdsPageHeader>
        </:header>
        <:alert>
          {{#if this.save.last.error}}
            <FormError />
          {{/if}}
          <TerraformPermissions as |Controls|>
            <Controls.GoToTerraform />
          </TerraformPermissions>
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @md="6">
              <Flex @direction="column" @gap="xl" data-test-user-details>
                <Flex @direction="column" @gap="xs">
                  <HdsTextDisplay
                    @tag="h4"
                    @size="300"
                    @weight="semibold"
                    data-test-user-edit-role-assignment-message
                  >
                    {{t
                      "components.page.access-control.users.edit-assignment.assign-an-organization-role"
                    }}
                  </HdsTextDisplay>
                  <HdsTextBody
                    @color="var(--token-color-palette-neutral-500)"
                    @tag="p"
                    @size="100"
                    @weight="regular"
                  >
                    <span data-test-user-edit-role-assignment-description>
                      {{t
                        "components.page.access-control.users.edit-assignment.this-role-defines-level-of-permissions-for"
                        organizationName=@organization.name
                        htmlSafe=true
                      }}
                    </span>
                    <HdsLinkInline
                      @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/users#user-permissions"
                      data-test-user-edit-role-assignment-learn-more-link
                    >
                      {{t
                        "components.page.access-control.users.edit-assignment.learn-more-about-hcp-permissions"
                      }}
                    </HdsLinkInline>
                  </HdsTextBody>
                </Flex>
                <form
                  class="hcp-hds-form"
                  data-test-user-edit-role-assignment-form
                  {{on "submit" this.save.perform}}
                >
                  <Flex @alignItems="start" @direction="column" @gap="xl">
                    <section>
                      <Flex @alignItems="start" @direction="column" @gap="xl">
                        <section>
                          <BasicRoleSelect
                            @roleId={{this.updatedRoleId}}
                            @onChange={{pick
                              "value"
                              (set this "updatedRoleId")
                            }}
                            data-test-user-edit-assignment-basic-role-select
                          >
                            <:afterSelect>
                              <HdsButton
                                @color="tertiary"
                                @icon="help"
                                @iconPosition="leading"
                                @isInline={{true}}
                                @text={{t
                                  "components.page.access-control.users.edit-assignment.what-can-each-role-do"
                                }}
                                {{on
                                  "click"
                                  (set this "showRoleDefinitionsFlyout" true)
                                }}
                                data-test-roles-chart-toggle-button
                              />
                            </:afterSelect>
                          </BasicRoleSelect>
                        </section>
                      </Flex>
                    </section>
                    <footer>
                      <Flex @alignItems="start" @direction="column" @gap="xl">
                        {{#unless this.updatedRoleId}}
                          <NoRoleWithProjects
                            data-test-user-edit-assignment-no-role-alert
                          />
                        {{/unless}}
                        <HdsButtonSet>
                          <HdsButton
                            @color="primary"
                            @text={{t "components.form.save"}}
                            disabled={{this.save.isRunning}}
                            type="submit"
                            data-test-submit
                          />
                          <HdsButton
                            @color="secondary"
                            @route="cloud.access-control.users.list"
                            @text={{t "components.form.cancel"}}
                            data-test-cancel
                          />
                        </HdsButtonSet>
                      </Flex>
                    </footer>
                  </Flex>
                </form>
              </Flex>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
      {{#if this.showRoleDefinitionsFlyout}}
        <RoleDefinitions
          @rolesLocation={{@currentRolesLocation}}
          @currentScopeLink={{@currentScopeLink}}
          @onClose={{set this "showRoleDefinitionsFlyout" false}}
        />
      {{/if}}
    </WithErrors>
  </template>
}
